import { useUser } from './useUser';
import versionInfo from '../components/UserLicense/versions/latest.json';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { locales } from '../utils/i18n';

const { version } = versionInfo[0];
const GA_MEASUREMENT_ID = import.meta.env.VITE_APP_GOOGLE_ANALYTICS_TAG;

export const sendPageview = (path: string) => {
  if (!window.gtag) return;
  window.gtag('event', 'page_view', {
    page_location: window.location.origin + path,
    page_path: path,
    page_title: document.title,
  });
};

export const sendGAEvent = (name: string, params?: Record<string, any>) => {
  if (!window.gtag) return;
  window.gtag('event', name, params);
};

const useGoogleAnalytics = () => {
  const GAInitialized = useRef(false);
  const { userDetails, auth, userSettings } = useUser();
  const accepted_terms_version = userDetails?.accepted_terms_version;
  const canTrackGA = accepted_terms_version === version;
  const location = useLocation();

  useEffect(() => {
    if (!GAInitialized.current && GA_MEASUREMENT_ID && auth?.id) {
      //initialize google analytics
      const script = document.createElement('script');
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
      script.async = true;
      document.head.appendChild(script);

      window.dataLayer = window.dataLayer || [];
      window.gtag = function () {
        window.dataLayer.push(arguments);
      };
      window.gtag('js', new Date());
      GAInitialized.current = true;
    }
  }, [canTrackGA, auth?.id]);

  useEffect(() => {
    if (canTrackGA) {
      sendPageview(location.pathname + location.search);
    }
  }, [location, canTrackGA]);

  useEffect(() => {
    if (canTrackGA && auth?.email && window.gtag) {
      const ui_details = userDetails?.ui_details ?? {};
      const { email, user_type = 'unknown' } = auth;
      const debug_mode = !import.meta.env.PROD && window.location.hostname === 'localhost';
      const isStrytenEmployee = email.endsWith('@stryten.com') || email.endsWith('@gnb.com');
      const langCode = (userSettings?.locale || 'en').toLowerCase().split('_')[0];
      const language = locales[langCode] ? locales[langCode] : 'English';
      // Hash email using SHA-256
      window.gtag('config', GA_MEASUREMENT_ID, { user_id: auth?.id, debug_mode });
      window.gtag('set', 'user_properties', {
        ...ui_details,
        isStrytenEmployee,
        userType: user_type,
        language,
      });
    }
  }, [auth?.email, canTrackGA, userSettings?.locale]);
};

export default useGoogleAnalytics;
